import React, { useEffect, useState, useCallback } from 'react';
import { useGuidedTour } from './GuidedTourContext';
import './GuidedTour.css';

const GuidedTour = () => {
  const { activeTour, stepIndex, nextStep, prevStep, endTour } = useGuidedTour();
  const [targetRect, setTargetRect] = useState(null);
  const [modalPosition, setModalPosition] = useState({ top: 100, left: 100 });

  // Keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (!activeTour) return;
    
    switch (event.key) {
      case 'Escape':
        endTour();
        break;
      case 'ArrowRight':
      case 'Enter':
        if (stepIndex < activeTour.steps.length - 1) {
          nextStep();
        } else {
          endTour();
        }
        break;
      case 'ArrowLeft':
        if (stepIndex > 0) {
          prevStep();
        }
        break;
      default:
        break;
    }
  }, [activeTour, stepIndex, nextStep, prevStep, endTour]);

  useEffect(() => {
    if (activeTour) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scrolling when tour is active
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [activeTour, handleKeyDown]);

  useEffect(() => {
    if (!activeTour) return;
    
    const step = activeTour.steps[stepIndex];
    if (!step) return;

    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      const targetEl = document.querySelector(step.target);
      if (targetEl) {
        // Scroll target element into view
        targetEl.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'center'
        });

        // Get updated position after scroll
        setTimeout(() => {
          const rect = targetEl.getBoundingClientRect();
          setTargetRect({
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            height: rect.height
          });

          // Calculate modal position with mobile-friendly logic
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const isMobile = viewportWidth < 768;
          const isSmallScreen = viewportWidth < 480;
          
          // Responsive modal dimensions
          let modalWidth = isSmallScreen ? Math.min(320, viewportWidth - 40) : 
                          isMobile ? Math.min(360, viewportWidth - 60) : 400;
          let modalHeight = 250;
          const padding = isMobile ? 10 : 20;
          
          let modalTop = rect.top + window.scrollY;
          let modalLeft = rect.right + window.scrollX + padding;

          // Mobile-first positioning strategy
          if (isMobile) {
            // On mobile, prioritize visibility over precise positioning
            // Try bottom first (more natural scroll behavior)
            modalLeft = Math.max(padding, Math.min(
              rect.left + window.scrollX + (rect.width - modalWidth) / 2,
              viewportWidth - modalWidth - padding
            ));
            modalTop = rect.bottom + window.scrollY + padding;
            
            // If bottom doesn't fit, try top
            if (modalTop + modalHeight > viewportHeight + window.scrollY - padding) {
              modalTop = Math.max(padding + window.scrollY, rect.top + window.scrollY - modalHeight - padding);
            }
            
            // If still doesn't fit vertically, center on screen
            if (modalTop < padding + window.scrollY || modalTop + modalHeight > viewportHeight + window.scrollY - padding) {
              modalTop = Math.max(padding + window.scrollY, 
                window.scrollY + (viewportHeight - modalHeight) / 2);
              
              // For very small screens, position near top for better visibility
              if (isSmallScreen) {
                modalTop = Math.max(padding + window.scrollY, window.scrollY + 60);
              }
            }
          } else {
            // Desktop positioning (existing logic)
            // Prefer right side first
            if (modalLeft + modalWidth > viewportWidth) {
              // Try left side
              modalLeft = rect.left + window.scrollX - modalWidth - padding;
              
              if (modalLeft < 0) {
                // Try bottom
                modalLeft = Math.max(padding, rect.left + window.scrollX + (rect.width - modalWidth) / 2);
                modalTop = rect.bottom + window.scrollY + padding;
                
                if (modalTop + modalHeight > viewportHeight + window.scrollY) {
                  // Try top
                  modalTop = rect.top + window.scrollY - modalHeight - padding;
                }
              }
            }
          }

          // Final bounds checking with mobile considerations
          if (isMobile) {
            // Ensure modal is always visible on mobile
            modalTop = Math.max(
              padding + window.scrollY, 
              Math.min(modalTop, window.scrollY + viewportHeight - modalHeight - padding)
            );
            modalLeft = Math.max(
              padding, 
              Math.min(modalLeft, viewportWidth - modalWidth - padding)
            );
          } else {
            // Desktop bounds checking
            modalTop = Math.max(padding, Math.min(modalTop, viewportHeight + window.scrollY - modalHeight - padding));
            modalLeft = Math.max(padding, Math.min(modalLeft, viewportWidth - modalWidth - padding));
          }

          setModalPosition({ top: modalTop, left: modalLeft });
          
          // Ensure modal is visible by scrolling if necessary (especially on mobile)
          if (isMobile) {
            setTimeout(() => {
              const modalElement = document.querySelector('.guided-tour-modal');
              if (modalElement) {
                const modalRect = modalElement.getBoundingClientRect();
                const isModalVisible = modalRect.top >= 0 && 
                                     modalRect.bottom <= viewportHeight &&
                                     modalRect.left >= 0 && 
                                     modalRect.right <= viewportWidth;
                
                if (!isModalVisible) {
                  modalElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'center'
                  });
                }
              }
            }, 100);
          }
        }, 100);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [activeTour, stepIndex]);

  if (!activeTour) return null;
  
  const step = activeTour.steps[stepIndex];
  if (!step) return null;

  // Calculate responsive width for modal
  const viewportWidth = window.innerWidth;
  const isMobile = viewportWidth < 768;
  const isSmallScreen = viewportWidth < 480;
  
  const modalWidth = isSmallScreen ? Math.min(320, viewportWidth - 40) : 
                    isMobile ? Math.min(360, viewportWidth - 60) : 400;

  return (
    <>
      {/* Dark overlay with precise rectangular cutout */}
      {targetRect && (
        <>
          {/* Top overlay */}
          <div
            className="guided-tour-overlay"
            style={{
              height: targetRect.top - 8
            }}
          />
          
          {/* Bottom overlay */}
          <div
            className="guided-tour-overlay"
            style={{
              top: targetRect.top + targetRect.height + 8,
              height: `calc(100vh - ${targetRect.top + targetRect.height + 8}px)`
            }}
          />
          
          {/* Left overlay */}
          <div
            className="guided-tour-overlay"
            style={{
              top: targetRect.top - 8,
              height: targetRect.height + 16,
              width: targetRect.left - 8
            }}
          />
          
          {/* Right overlay */}
          <div
            className="guided-tour-overlay"
            style={{
              top: targetRect.top - 8,
              left: targetRect.left + targetRect.width + 8,
              height: targetRect.height + 16,
              width: `calc(100vw - ${targetRect.left + targetRect.width + 8}px)`
            }}
          />
        </>
      )}
      
      {/* Full overlay if no target */}
      {!targetRect && (
        <div
          className="guided-tour-overlay"
          style={{
            background: 'rgba(0,0,0,0.75)'
          }}
        />
      )}
      
      {/* Highlighted element border */}
      {targetRect && (
        <div
          className="guided-tour-highlight"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8
          }}
        />
      )}

      {/* Tour modal */}
      <div
        className="guided-tour-modal"
        style={{
          top: modalPosition.top,
          left: modalPosition.left,
          width: modalWidth
        }}
      >
        {/* Tour header */}
        <div className="guided-tour-header">
          <h3 className="guided-tour-title">
            {step.title}
          </h3>
          <button
            className="guided-tour-close"
            onClick={endTour}
            title="Close tour (Esc)"
          >
            ×
          </button>
        </div>

        {/* Tour content */}
        <div className="guided-tour-content">
          {step.content}
        </div>

        {/* Tour navigation */}
        <div className="guided-tour-navigation">
          <button 
            className="guided-tour-button guided-tour-button-back"
            onClick={prevStep} 
            disabled={stepIndex === 0}
            title="Previous step (←)"
          >
            Back
          </button>
          
          <span className="guided-tour-step-indicator">
            {stepIndex + 1} of {activeTour.steps.length}
          </span>
          
          {stepIndex < activeTour.steps.length - 1 ? (
            <button 
              className="guided-tour-button guided-tour-button-next"
              onClick={nextStep}
              title="Next step (→ or Enter)"
            >
              Next
            </button>
          ) : (
            <button 
              className="guided-tour-button guided-tour-button-finish"
              onClick={endTour}
              title="Finish tour (Enter)"
            >
              Finish
            </button>
          )}
        </div>

        {/* Keyboard shortcuts hint */}
        <div style={{ 
          marginTop: 12, 
          fontSize: '11px', 
          color: '#999', 
          textAlign: 'center' 
        }}>
          Use ← → arrow keys or Esc to close
        </div>
      </div>
    </>
  );
};

export default GuidedTour;
